import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTimezone } from '@uplift/contexts';
import { useChangeSessionReasonsQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { ChangeSessionReason, ChangeSessionReasonTypes, UserRole } from '@uplift/types';

import { Autocomplete } from '../../../Inputs';

export type CancelSessionReasonAutocompleteProps = {
  name: string;
  label: string;
  userRole: keyof typeof UserRole;
  fromDatetime: string;
  excludeReasons?: string[];
};


const useStyles = makeStyles()(theme => ({
  input: {
    paddingRight: theme.spacing(6),
  }
}));


export const CancelSessionReasonAutocomplete: React.FC<CancelSessionReasonAutocompleteProps> = ({ name, label, userRole, fromDatetime, excludeReasons }) => {
  const { classes } = useStyles();
  const timezone = useTimezone();
  const { fetching, reasons: _reasons } = useChangeSessionReasonsQuery(UserRole[userRole], ChangeSessionReasonTypes.cancel);
  const reasons = React.useMemo(() => {
    if (!excludeReasons) {
      return _reasons;
    }

    const excludeReasonsSet = new Set<string | undefined>(excludeReasons);

    return _reasons.filter(r => !excludeReasonsSet.has(r.name));
  }, [ _reasons, excludeReasons ]);

  const isAdmin = userRole === 'superadmin';
  const isInPast = React.useMemo(() => {
    const now = CalendarDateService.now(timezone);

    return CalendarDateService.date(fromDatetime).tz(timezone).isBefore(now);
  }, [ timezone, fromDatetime ]);

  const getOptionLabel = React.useCallback((option: ChangeSessionReason) => {
    let label: string | null | undefined;

    if (isAdmin) {
      label = option.adminDescription;
    } else if (isInPast) {
      label = option.descriptionPastTense;
    }

    return label ?? option.description ?? '';
  }, [ isAdmin, isInPast ]);

  return (
    <Autocomplete
      classes={classes}
      name={name}
      label={label}
      disabled={fetching}
      options={reasons}
      getOptionLabel={getOptionLabel}
      transformValue={opt => opt?.id}
    />
  );
};
